import { useEffect, useState } from 'react';
import { Chip, CircularProgress, Divider, Skeleton, Tooltip } from '@mui/material';
import { BsDot } from 'react-icons/bs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { get_payment_plans } from '../Redux/Action/PaymentAction';
import axios from 'axios';
import useTitle from '../Hooks/useTitle';
import FaceIcon from '@mui/icons-material/Face';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const Rate = () => {
    useTitle('Plans & Pricing');
    const { t } = useTranslation('rate');
    const dispatch = useDispatch();
    const { loading, payment_plans } = useSelector((state) => state.Payment);
    const [filteredPlans, setFilteredPlans] = useState([]);
    const [activePlan, setActivePlan] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [clickedPlanId, setClickedPlanID] = useState(null);

    useEffect(() => {
        dispatch(get_payment_plans());
    }, [dispatch]);

    useEffect(() => {
        if (!payment_plans?.data?.length) return;

        const plans = payment_plans.data;
        const active = plans.find(plan => plan.is_active);
        setActivePlan(active || null);

        const basePlans = ["basic", "pro", "premium"];
        const allowedPlansMap = {
            "basic": ["basic", "basic_pro", "basic_premium"],
            "pro": ["basic", "pro", "pro_premium"],
            "premium": ["basic", "pro", "premium"],
            "basic_pro": ["basic", "basic_pro", "pro_premium"],
            "basic_premium": ["basic", "pro", "basic_premium"],
            "pro_premium": ["basic", "pro", "pro_premium"]
        };

        const allowedPlans = allowedPlansMap[active?.base_plan_upgrade] || basePlans;
        setFilteredPlans(plans.filter(plan => allowedPlans.includes(plan.base_plan_upgrade)));
    }, [payment_plans]);

    const isButtonDisabled = (plan) => {
        if (!activePlan) return false; // No active plan, all plans enabled
        const disabledPlansMap = {
            "none": [],
            "basic": ["basic"],
            "pro": ["basic", "pro"],
            "premium": ["basic", "pro", "premium"],
            "basic_pro": ["basic", "basic_pro"],
            "basic_premium": ["basic", "pro", "basic_premium"],
            "pro_premium": ["basic", "pro", "pro_premium"]
        };
        return disabledPlansMap[activePlan.base_plan_upgrade]?.includes(plan.base_plan_upgrade);
    };

    const purchasePlan = async (plan_id) => {
        setLoadingPlan(true);
        setClickedPlanID(plan_id);
        const authToken = localStorage.getItem('authToken');
        try {
            const response = await axios.get(`https://api.happiclips.com/api/buy-plan/${plan_id}`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            window.location.replace(response.data.data.url);
        } catch (error) {
            console.error("Purchase failed:", error);
        }
        finally {
            setLoadingPlan(false);
            setClickedPlanID(null);
        }
    };

    return (
        <div className="grid gap-6 p-8 py-6 mt-[76px] sm:ml-[300px]">
            <div className="items-center z-[1]">
                <span className='font-[600] text-[26px]'>{t('title')}</span>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-9">
                    {[1, 2, 3].map((_, i) => (
                        <div key={i} className="rounded-[20px] w-full">
                            <Skeleton variant="rectangular" width="100%" height="380px" className="rounded-[20px]" />
                            <div className="p-2">
                                <Skeleton width="90%" height={20} />
                                <Skeleton width="80%" height={20} />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 w-full gap-8">
                    {filteredPlans.map((plan) => {
                        const disabled = isButtonDisabled(plan);
                        const color = `${plan.remaining_guests === 0 ? '#ED3939' : '#3686FF'}`
                        return (
                            <div key={plan.id} className="relative bg-white w-full p-6 rounded-[15px] text-center grid gap-[20px]"
                                style={{ boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px' }}>
                                {plan.is_active && (
                                    <div className="absolute right-3 top-3 bg-[#3686FF] text-white text-[14px] font-bold py-1 px-3 rounded-md">
                                        Active
                                    </div>
                                )}

                                <div className="rounded-[10px] p-4 grid gap-4">
                                    <div className="grid gap-1 mt-5">
                                        <p className='text-[22px] font-[800] text-[#131415]'>{plan.name}</p>
                                    </div>
                                    <div className="text-[18px] text-[#131415] grid gap-3">
                                        <p className='font-[500]'>Nombre d'invités</p>
                                        <p className='font-[600]'>
                                            {plan.is_unlimited ? `+ de ${plan.min_guest}` : `Entre ${plan.min_guest} et ${plan.max_guest}`}
                                        </p>
                                        {plan.is_active && plan.max_guest && (
                                            <div className="mt-2">
                                                {/* <Tooltip title='Guest slots still available' placement="top"> */}
                                                <Chip
                                                    icon={<FaceIcon style={{ color: `${color}` }} />}
                                                    label={plan.is_unlimited
                                                        ? "No Guests Limit"
                                                        : `${plan.remaining_guests} Guest slots left`}
                                                    variant="outlined"
                                                    sx={{
                                                        color: `${color}`,
                                                        borderColor: `${color}`,
                                                    }}
                                                />
                                                {/* </Tooltip> */}
                                            </div>
                                        )}
                                        <p className='font-[600]'>{`${parseInt(plan.price)} € INCL. VAT`}</p>
                                    </div>

                                    <Tooltip
                                        title={
                                            disabled ? (
                                                <div className="tooltip-content">
                                                    {
                                                        plan.is_active ? 'Your Active Plan' : 'You cannot downgrade your plan'
                                                    }

                                                    <div className="tooltip-arrow "></div>
                                                </div>
                                            ) : ""
                                        }
                                        placement="bottom"
                                        arrow
                                    >
                                        <span> {/* Span is needed to wrap disabled buttons for Tooltip to work */}
                                            <button
                                                className={`rounded-lg h-[52px] w-full bg-[#3686FF] ${disabled ? 'opacity-50' : ''} text-white mb-2`}
                                                onClick={() => !disabled && purchasePlan(plan.id)}
                                                disabled={disabled}
                                                style={{ cursor: disabled ? "not-allowed" : "pointer" }}
                                            >
                                                {loadingPlan && clickedPlanId === plan.id
                                                    ? <CircularProgress size={30} color="inherit" sx={{ mt: 1 }} />
                                                    : plan.is_active
                                                        ? "Your current plan"
                                                        : disabled
                                                            ? "DownGrade"
                                                            : activePlan
                                                                ? "Upgrade"
                                                                : "Purchase"
                                                }
                                            </button>
                                        </span>
                                    </Tooltip>
                                </div>
                                {
                                    plan.is_active && (
                                        <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2">
                                            <div className="flex items-center bg-gradient-to-r text-dark px-3 py-1">
                                                <CalendarTodayIcon className="mr-1" style={{ fontSize: '18px' }} />
                                                <span className="text-xs font-bold whitespace-nowrap">
                                                    Expires on {new Date(plan.expires_at).toLocaleDateString("fr-FR")}
                                                </span>
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        );
                    })}
                </div>
            )}

            {
                loading ?
                    (
                        <div className="p-2">
                            <Skeleton width="100%" height={50} />
                            <Skeleton width="100%" height={40} />
                        </div>
                    ) : (
                        <div className="bg-white py-4 flex flex-col gap-4 rounded-[20px] mt-3">
                            <span className='text-[18px] font-[600] text-[#000000] px-6'>{t('all_subscriptions_includes')}</span>
                            <Divider />
                            <div className="px-6 grid gap-4">
                                {["video", "Personalized", "Upload"].map((item, i) => (
                                    <div className="flex" key={i}>
                                        <BsDot size={20} />
                                        <p className='text-[#131415] font-[500] text-[15px]'>{t(item)}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )
            }

        </div>
    );
};

export default Rate;
